import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import {
  Container,
  Divider,
  Box,
  Grid,
  GridItem,
  Spinner,
  Flex,
  Button,
} from '@chakra-ui/react';
import Breadcrumbs from '../../../common/Breadcrumbs';
import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import PassesViewHeader from './PassesViewHeader';
import { useGetPassQuery } from '../../../store/redux/api/passApi';
import { useGetPassTemplateQuery } from '../../../store/redux/api/templateApi';
import PassBuilderPanel from './PassBuilderPanel';
import PassErrorsBanner from './PassErrorsBanner';
import BuilderBanner from '../pass-templates-new/BuilderBanner';
import type { PassBarcode } from '../../../store/types/pass';
import PreviewPanel from '../pass-templates-new/PreviewPanel';
import { buildTemplateMeta } from './utils/buildTemplateMeta';
import {
  buildDevModeData,
  getAllTemplateFields,
} from './utils/buildPassPayload';
import { truncate } from './utils/passHelpers';
import {
  mapFeaturedActionsFromApi,
  type FeaturedActionsApiInput,
} from '../pass-templates-new/utils/mapApiResponseToTemplateState';
import { usePass } from './hooks/usePass';
import { usePassPreviewData } from './hooks/usePassPreviewData';
import { usePassSave } from './hooks/usePassSave';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  selectPass,
  setFocusedField as setPassFocusedField,
} from '../../../store/redux/slices/pass.slice';
import { resetTemplate } from '../../../store/redux/slices/template.slice';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

const PassesBuilderPage = ({
  isEdit,
  isCreate,
}: {
  isEdit?: boolean;
  isCreate?: boolean;
}) => {
  const { uuid } = useParams<{ uuid: string }>();
  const { hash } = useLocation();
  const dispatch = useAppDispatch();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [selectedPassLanguage, setSelectedPassLanguage] = useState<
    string | null
  >(null);
  const [activePassTab, setActivePassTab] = useState<'Front' | 'Back'>('Front');
  const meCtx = useContext(MeContext) as {
    state: MeContextState & { localisationEnabled?: boolean };
  };
  const localisationEnabled = !!meCtx.state.localisationEnabled;

  useEffect(() => {
    dispatch(resetTemplate());
  }, [dispatch]);

  const setFieldError = useCallback((path: string, message: string | null) => {
    setValidationErrors((prev) => {
      if (message) {
        if (prev[path] === message) return prev;
        return { ...prev, [path]: message };
      }
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  // ---- Data fetching ----

  const passState = useAppSelector(selectPass);
  const focusedField = passState.focusedField;

  const {
    data: passData,
    isLoading,
    isError,
  } = useGetPassQuery({ uuid: uuid ?? '' }, { skip: !uuid });
  const pass = passData?.data?.data;
  const passTemplate = passData?.meta?.template;
  const templateId = passTemplate?.uuid || passState.templateUuid || '';

  const { currentData: templateDetail } = useGetPassTemplateQuery(
    { id: templateId, includeDynamicForm: true },
    { skip: !templateId, refetchOnMountOrArgChange: true }
  );
  // ---- Pass slice ----

  const { handleSyncFromTemplate } = usePass(pass);
  const featuredActionsSyncedForTemplate = useRef<string | null>(null);

  useEffect(() => {
    if (!passState.templateUuid)
      featuredActionsSyncedForTemplate.current = null;
  }, [passState.templateUuid]);

  // Sync NFC/barcode from selected template on create
  useEffect(() => {
    if (!isCreate || !templateDetail?.data) return;
    const attrs = templateDetail.data.attributes as Record<string, unknown>;
    const tBarcode = attrs.barcode as
      | {
          enabled?: boolean;
          type?: string;
          source?: string;
          value?: string;
          displayText?: boolean;
        }
      | undefined;
    const tNfc = attrs.nfc as
      | { enabled?: boolean; source?: string; customValue?: string }
      | undefined;

    const templateFeaturedActions = (
      attrs.appleEnhancedDesign as
        | { featuredActions?: FeaturedActionsApiInput }
        | undefined
    )?.featuredActions;

    const loadedTemplateId = templateDetail.data.id;
    const syncFeaturedActions =
      !!loadedTemplateId &&
      featuredActionsSyncedForTemplate.current !== loadedTemplateId;
    if (syncFeaturedActions) {
      featuredActionsSyncedForTemplate.current = loadedTemplateId;
    }

    handleSyncFromTemplate({
      barcode: {
        enabled: tBarcode?.enabled ?? false,
        type: (tBarcode?.type as PassBarcode['type']) ?? null,
        source: (tBarcode?.source as PassBarcode['source']) ?? null,
        value: tBarcode?.value ?? '',
        displayText: tBarcode?.displayText ?? false,
      },
      nfcEnabled: tNfc?.enabled ?? false,
      nfcSource: tNfc?.source ?? 'uuid',
      nfcCustomValue: tNfc?.customValue ?? '',
      ...(syncFeaturedActions && {
        featuredActions: mapFeaturedActionsFromApi(templateFeaturedActions),
      }),
    });
  }, [isCreate, templateDetail, handleSyncFromTemplate]);

  // ---- Preview ----

  const { template, templateAttrsRaw, previewData } = usePassPreviewData({
    pass,
    passState,
    passTemplate,
    templateDetail,
    isEdit,
    isCreate,
    selectedPassLanguage,
    uuid,
  });

  const passDefaultLanguage =
    (templateAttrsRaw?.defaultLanguage as string) || 'en';
  const passLanguages = useMemo(() => {
    if (!localisationEnabled) return [];
    const fields = templateAttrsRaw?.fields as
      | Record<
          string,
          Record<string, { localisation?: Record<string, unknown> } | null>
        >
      | undefined;
    if (!fields) return [];
    const langs = new Set<string>();
    Object.values(fields).forEach((section) => {
      if (!section) return;
      Object.values(section).forEach((field) => {
        if (field?.localisation) {
          Object.keys(field.localisation).forEach((l) => langs.add(l));
        }
      });
    });
    return Array.from(langs);
  }, [templateAttrsRaw, localisationEnabled]);

  const isReadOnly = useIsReadOnly();
  const readOnlyInertProps = isReadOnly
    ? ({ inert: '' } as unknown as { inert?: boolean })
    : {};

  // ---- Save ----

  const { handleSave, isSaving } = usePassSave({
    isEdit,
    isCreate,
    uuid,
    passState,
    template,
    templateDetail,
    templateId,
    pass,
    localisationEnabled,
    setValidationErrors,
  });

  // ---- Dev mode ----

  const devModeData = useMemo(
    () =>
      buildDevModeData(
        passState,
        localisationEnabled,
        pass?.attributes?.passContent as Record<string, unknown> | undefined,
        template ? getAllTemplateFields(template) : undefined
      ),
    [passState, localisationEnabled, pass, template]
  );

  // ---- Side effects ----

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const timer = setTimeout(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
    return () => clearTimeout(timer);
  }, [hash]);

  // ---- Render ----

  const passIdentifier = truncate(pass?.attributes?.extId || pass?.id, 24);
  const breadcrumbLinks = [
    { linkName: 'Home', linkUrl: '/' },
    { linkName: 'passes', linkUrl: '/passes' },
    {
      linkName: isEdit ? passIdentifier : 'create pass',
      linkUrl: isEdit ? `/passes/${uuid}` : undefined,
    },
    ...(isEdit ? [{ linkName: 'edit pass', linkUrl: undefined }] : []),
  ];

  if (isLoading) {
    return (
      <Spinner
        width="52px"
        height="52px"
        thickness="4px"
        speed="0.65s"
        emptyColor="quinaryBackground"
        m="auto"
      />
    );
  }

  if (isEdit && (isError || (!isLoading && !pass))) {
    return <Navigate to="/passes" replace />;
  }

  return (
    <Container display="flex" flexDir="column" flex="1" pb={6}>
      <BuilderBanner
        title="Welcome to the updated pass manager"
        subtitle="We've made improvements to help you issue and manage passes more easily."
        simpleMode
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <PassesViewHeader isEdit={isEdit} isCreate={isCreate} />
      {(isEdit || isCreate) && <PassErrorsBanner errors={validationErrors} />}
      <Divider borderColor="#ADADAD" mt={4} mb={10} />
      <Grid templateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={10}>
        <GridItem minW={0}>
          <Box {...readOnlyInertProps} opacity={isReadOnly ? 0.6 : 1}>
            <PassBuilderPanel
              isEdit={isEdit}
              isCreate={isCreate}
              errors={validationErrors}
              setFieldError={setFieldError}
              onActiveSection={(section, locale) => {
                setActivePassTab(section === 'back' ? 'Back' : 'Front');
                setSelectedPassLanguage(locale ?? null);
              }}
              onFieldFocus={(id) => dispatch(setPassFocusedField(id))}
              onClearErrors={() => setValidationErrors({})}
              focusedField={focusedField}
            />
          </Box>
        </GridItem>
        <GridItem minW={0}>
          <Box position={{ base: 'static', md: 'sticky' }} top={{ md: 6 }}>
            <PreviewPanel
              externalPreviewData={previewData}
              previewEntityId={template?.entity_id}
              selectedPassTab={activePassTab}
              isPassPreview
              devModeData={devModeData}
              passLanguages={passLanguages}
              passDefaultLanguage={passDefaultLanguage}
              selectedPassLanguage={selectedPassLanguage}
              onPassLanguageChange={setSelectedPassLanguage}
              passDeviceType={pass?.attributes?.deviceType}
            />
            {(isEdit || isCreate) && (
              <Flex justify="flex-end">
                {isReadOnly ? (
                  <LockedActionButton mt={4}>
                    {isEdit ? 'Save' : 'Create Pass'}
                  </LockedActionButton>
                ) : (
                  <Button
                    onClick={handleSave}
                    isLoading={isSaving}
                    mt={4}
                    disabled={isCreate && !passState.templateUuid}
                  >
                    {isEdit ? 'Save' : 'Create Pass'}
                  </Button>
                )}
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>
    </Container>
  );
};

export default PassesBuilderPage;
